// ============================================================================
// COURSE CARD — LifeOS School.dc.html, option 6a, the 2×2 grid.
//
// White, radius 16, 1px line, a 5px full-height rounded bar in the course hue on the left edge.
// Code · units · grade chip · unread chip, the title beneath, then NEXT and DUE, then the
// transcription pipeline chips.
//
// THE GRADE CHIP IS THE POINT OF THIS CARD. Three states, three different sentences:
//   score   '92.1% A−'                 — a real Canvas score, and Canvas's own letter
//   hidden  'grade hidden'             — the instructor set hide_final_grades
//   none    'not graded yet'           — nothing has been marked
// A blank chip would collapse the last two into each other and quietly imply the third.
// ============================================================================

import { Link } from 'react-router-dom'
import {
  courseGrade,
  gradeChipLabel,
  courseSlug,
  isDatedAssignment,
  type Assignment,
  type Course,
  type Lecture,
  type Term,
} from '@app/shared'

import type { TimeTone } from '../../lib/time'
import {
  hueBg,
  hueUnreadPill,
  lecturePipelineCounts,
  nextDeadlinePhrase,
  nextMeeting,
  nextMeetingPhrase,
} from './derive'
import { Spinner } from './Chrome'
import { AssignmentPeek, useAssignmentPeek } from './AssignmentPeek'
import { ValueSwap } from '../motion/ValueSwap'

const DUE_TONE: Record<TimeTone, string> = {
  overdue: 'text-danger-2',
  urgent: 'text-orange',
  calm: 'text-ink-2',
}

export function CourseCard({
  course,
  term,
  lectures,
  nextDeadline,
  unread,
  today,
  nowMinutes,
}: {
  course: Course
  term: Term | null | undefined
  lectures: Lecture[]
  nextDeadline: Assignment | undefined
  /** Counted from the announcements list, not read off the course row — see AnnouncementList. */
  unread: number
  today: string
  nowMinutes: number
}) {
  const grade = courseGrade(course)
  const meeting = nextMeeting(course, term, today, nowMinutes)
  // A card's DUE line is a countdown, so it can only speak for work that HAS a deadline.
  const due =
    nextDeadline && isDatedAssignment(nextDeadline)
      ? nextDeadlinePhrase(nextDeadline, today, nowMinutes)
      : null
  const pipeline = lecturePipelineCounts(lectures)
  const peek = useAssignmentPeek()

  return (
    // A FRAGMENT, so the peek is the card's SIBLING and not its child. Inside the `<Link>` its own
    // "Open in bCourses" anchor would be an <a> inside an <a> — invalid markup, and a click on it
    // would navigate to the course page at the same time. The panel is `position: fixed`, so
    // being a second child of the 2×2 grid costs it no track.
    <>
    <Link
      to={`/school/${courseSlug(course.code)}`}
      className="flex flex-col gap-[13px] rounded-context border border-line bg-surface px-[19px] py-[17px] shadow-card transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)] hover:bg-surface-2"
    >
      <div className="flex items-start gap-[11px]">
        <span
          className={['w-[5px] flex-none self-stretch rounded-[3px]', hueBg(course.code)].join(' ')}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[9px]">
            <span className="text-[14.5px] font-semibold leading-[1.1]">{course.code}</span>
            {/* Canvas has no credit-hours field, so the segment is omitted rather than
                rendered as "null units" or invented from the course code. */}
            {course.units !== null && (
              <span className="text-[11px] font-medium leading-none text-muted">
                {course.units} units
              </span>
            )}

            {grade.kind === 'score' && (
              // 13f · A GRADE LANDS. When the score CHANGES it rises into place (translateY(9px)
              // → 0); when it comes back the same it does not move. `gradeChipLabel` already
              // carries the letter and the percentage together, so the number and its context
              // arrive in the same beat — which is 13f's actual rule.
              //
              // WHAT IS DELIBERATELY NOT HERE: the brief's class median. Canvas returns no class
              // median and this model has no field for one, so there is nothing to render
              // underneath the number. Inventing a median would be the app faking knowledge
              // (CLAUDE.md → product identity), which outranks matching the reference frame.
              <ValueSwap
                value={gradeChipLabel(grade)}
                className="rounded-pill bg-fill-2 px-[7px] py-[2px] text-[10px] font-semibold leading-[1.5] text-ink-2"
              >
                {gradeChipLabel(grade)}
              </ValueSwap>
            )}
            {grade.kind === 'hidden' && (
              // Quieter than a score, and it must never be mistaken for one — but it is present,
              // because the absence of a chip would read as "nothing graded".
              //
              // 13f's other half: THE SHIMMER THAT NEVER RESOLVES. A slow opacity breath, 3s,
              // indefinitely — the one animation in the app that resolves into nothing, because
              // there is nothing to resolve into. Not a spinner, not a dash, not an empty cell:
              // a spinner would promise an arrival, and nothing is arriving.
              <span
                className="mo-withheld rounded-pill bg-surface-4 px-[7px] py-[2px] text-[10px] font-semibold leading-[1.5] text-microcopy"
                title="The instructor turned grades off in Canvas. A score exists; Canvas will not return it."
              >
                grade hidden
              </span>
            )}
            {grade.kind === 'none' && (
              <span
                className="rounded-pill bg-surface-4 px-[7px] py-[2px] text-[10px] font-semibold leading-[1.5] text-microcopy"
                title="Canvas has computed no score for this course yet — nothing has been marked."
              >
                not graded yet
              </span>
            )}

            {unread > 0 && (
              <span
                className={[
                  'ml-auto rounded-pill px-[7px] py-[2px] text-[10px] font-bold leading-[1.5]',
                  hueUnreadPill(course.code),
                ].join(' ')}
              >
                {unread} new
              </span>
            )}
          </div>
          <p className="mt-[5px] text-[12px] leading-[1.4] text-ink-3">{course.name}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Row label="NEXT">
          {meeting ? (
            <span className="text-ink-2">{nextMeetingPhrase(meeting, today)}</span>
          ) : (
            // Not "—". A course with no next meeting has finished for the term, or the term
            // itself has not been synced, and those are different sentences.
            // Three absences, three sentences. A course Canvas gave no timetable for has NOT
            // finished for the term — Berkeley's meeting times are not in Canvas at all.
            <span className="font-normal text-microcopy">
              {course.meetings.length === 0
                ? 'no meeting times in Canvas'
                : term
                  ? 'no more meetings this term'
                  : 'term not synced yet'}
            </span>
          )}
        </Row>
        <Row label="DUE">
          {due && nextDeadline ? (
            // THE ONE INTERACTIVE THING INSIDE THE CARD'S LINK (2026-08-29). The card as a whole
            // still goes to the course page; this phrase names ONE assignment, and clicking the
            // name of a thing should tell you about that thing rather than about its course.
            //
            // `preventDefault` is what keeps the two gestures apart: without it the click would
            // open the peek AND navigate away from it in the same beat. `stopPropagation` keeps
            // the row's own click out of the card's handler.
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                peek.openFor(nextDeadline, event.currentTarget)
              }}
              aria-expanded={peek.open?.assignment.id === nextDeadline.id}
              aria-haspopup="dialog"
              className={[
                'rounded-button text-left underline-offset-2 hover:underline',
                DUE_TONE[due.tone],
              ].join(' ')}
            >
              {due.text}
            </button>
          ) : (
            <span className="font-normal text-microcopy">nothing open</span>
          )}
        </Row>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-[2px]">
        {pipeline.transcribed > 0 && (
          <Chip className="bg-success-fill text-success">
            <span className="size-[5px] flex-none rounded-full bg-live-dot" aria-hidden />
            {pipeline.transcribed} {pipeline.transcribed === 1 ? 'lecture' : 'lectures'} transcribed
          </Chip>
        )}
        {pipeline.processing > 0 && (
          <Chip className="bg-surface-4 text-muted">
            <Spinner />
            {pipeline.processing} processing
          </Chip>
        )}
        {pipeline.queued > 0 && (
          <Chip className="bg-info-fill text-info">
            {pipeline.queued} queued
          </Chip>
        )}
        {pipeline.recorded === 0 && (
          <Chip className="bg-surface-4 text-microcopy">no lectures recorded</Chip>
        )}
      </div>
    </Link>

    {peek.open && (
      <AssignmentPeek
        assignment={peek.open.assignment}
        course={course}
        anchor={peek.open.anchor}
        onClose={peek.close}
      />
    )}
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-[9px]">
      <span className="w-[44px] flex-none text-[10px] font-semibold leading-none tracking-[0.06em] text-muted">
        {label}
      </span>
      <span className="min-w-0 truncate text-[12.5px] font-medium leading-[1.3]">{children}</span>
    </div>
  )
}

function Chip({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={[
        'flex items-center gap-[6px] whitespace-nowrap rounded-pill px-[9px] py-1 text-[10.5px] font-semibold leading-none',
        className,
      ].join(' ')}
    >
      {children}
    </span>
  )
}
